import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  AssetItemDto,
  AssetVersionCommentDto,
  AssetVersionItemDto
} from '@api/Api';
import FabricContextProvider from '@context/FabricContext';
import {
  AssetViewerStateProvider,
  type IAssetViewerStateContextValue
} from '@context/AssetViewerStateContext';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import AssetViewer from '@pages/MediaViewer/AssetViewer';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import SidebarPanelDismissHandler from '@pages/MediaViewer/components/SidebarPanelDismissHandler';

const Comments = React.lazy(() => import('@pages/MediaViewer/Comments'));

type CompareVersionSideProps = {
  selectedVersion: AssetVersionItemDto;
  commentsList: AssetVersionCommentDto[];
  isLoadingCommentsList: boolean;
  commentsOpen: boolean;
  onCloseCommentsPanel: () => void;
  asset: AssetItemDto;
  canCreateTask: boolean;
  setAssetViewerState: (
    assetViewerState: IAssetViewerStateContextValue | null
  ) => void;
};

function ExposeAssetViewerStateToParent({
  setAssetViewerState
}: {
  setAssetViewerState: (
    assetViewerState: IAssetViewerStateContextValue | null
  ) => void;
}) {
  const ctx = useAssetViewerState();
  useEffect(() => {
    setAssetViewerState(ctx);
    return () => {
      setAssetViewerState(null);
    };
  }, [setAssetViewerState, ctx]);
  return null;
}

function CompareVersionSide({
  selectedVersion,
  commentsList,
  isLoadingCommentsList,
  commentsOpen,
  onCloseCommentsPanel,
  asset,
  canCreateTask,
  setAssetViewerState
}: CompareVersionSideProps) {
  const commentsPanelRef = useRef<HTMLDivElement>(null);
  const [isPanelPinned, setIsPanelPinned] = useState(false);

  const onTogglePanelPin = useCallback(() => {
    setIsPanelPinned((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!commentsOpen) {
      setIsPanelPinned(false);
    }
  }, [commentsOpen]);

  return (
    <FabricContextProvider>
      <AssetViewerStateProvider
        commentsList={commentsList}
        selectedVersion={selectedVersion}
      >
        <SidebarPanelDismissHandler
          sidebarRef={commentsPanelRef}
          isOpen={commentsOpen}
          isPinned={isPanelPinned}
          isLgWidth
          onClose={onCloseCommentsPanel}
        />
        <ExposeAssetViewerStateToParent
          setAssetViewerState={setAssetViewerState}
        />
        <AssetViewer isShowShapeDrawingTools={false} />
        {!commentsOpen && (
          <FloatCommentEditor
            asset={asset}
            canCreateTask={canCreateTask}
            commentMentions={false}
            selectedVersion={selectedVersion}
          />
        )}
        {!commentsOpen && (
          <FloatCommentItem
            asset={asset}
            selectedVersion={selectedVersion}
            commentMentions={false}
            canCreateTask={canCreateTask}
          />
        )}
        {commentsOpen && (
          <div
            ref={commentsPanelRef}
            className="compare-versions-modal_body-comments"
          >
            <Suspense fallback={null}>
              <Comments
                canCreateTask={canCreateTask}
                selectedVersion={selectedVersion}
                commentsList={commentsList}
                isLoadingCommentsList={isLoadingCommentsList}
                asset={asset}
                commentMentions={false}
                onClosePanel={onCloseCommentsPanel}
                isPanelPinned={isPanelPinned}
                onTogglePanelPin={onTogglePanelPin}
              />
            </Suspense>
          </div>
        )}
      </AssetViewerStateProvider>
    </FabricContextProvider>
  );
}

export default CompareVersionSide;
